<template>
  <WithLoader :isLoading="isLoading" classType="primary">
    <h1>General Info</h1>
    <div class="campaigns-create__template-wrapper">
      <v-form
        extraClass="generalInfo"
        :name="name"
        :fields="fields"
        hideSubmitBtn
      ></v-form>
    </div>

    <div class="campaigns-create__address-wrapper">
      <div class="form-top-wrapper">
        <h1>Address</h1>
        <div class="account-forms-wrapper">
          <v-form
            :name="name"
            :fields="toggleField"
            :initialValues="{ useAccount: this.shouldUseAccount }"
            hideSubmitBtn
          >
          </v-form>
        </div>
      </div>

      <div class="campaign-address-fields">
        <v-form
          v-show="useAccount === 'no'"
          :name="addressFormName"
          :fields="addressFields"
          hideSubmitBtn
        ></v-form>
      </div>
    </div>
  </WithLoader>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapGetters, mapActions } from "vuex";
import map from "lodash/map";

// components
import VForm from "@/components/VForm";

// mixins
import withTags from "@/mixins/withTags";
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";
import withFormUtils from "@/mixins/withFormUtils";

import { required, maxLength, requiredTags } from "@/utils/validations";
import { addressFormFields } from "@/constants/forms";
import {
  convertTagsToSelectListFormat,
  convertTemplatesToSelectFormat
} from "@/utils/util";

export default Vue.component("CampaignsCreateForm", {
  props: {
    templateType: VueTypes.oneOf(["postcard", "letter"]).def("postcard"),
    campaignToEdit: VueTypes.string,
    initialCampaignValues: VueTypes.object
  },

  mixins: [withTags, withToasted, withNavigate, withFormUtils],

  components: {
    VForm
  },

  data() {
    return {
      name: "newCampaign",
      addressFormName: "createCampaignAddress",
      toggleField: [
        {
          name: "useAccount",
          label: "Use account address",
          classType: "primary",
          elementType: "switcher",
          extraClass: "use-address",
          switcherType: "toggleReverse",
          switcherKeys: {
            left: {
              key: "no",
              label: "Use Account Address"
            },

            right: {
              key: "yes",
              label: ""
            }
          }
        }
      ]

      // initialCampaignValues: this.getCampaign(this.campaignToEdit),
    };
  },

  computed: {
    _templatesSelectData() {
      const templatesData =
        this.templateType === "postcard"
          ? this.postcardsTemplatesList
          : this.lettersTemplatesList;
      return convertTemplatesToSelectFormat(templatesData);
    },

    _isTagsLoading() {
      return this.isMainTagsLoading || this.isFormTagsLoading;
    },

    fields() {
      return [
        {
          label: "Name",
          placeholder: "Outreach campaign",
          name: "name",
          classType: "primary",
          isRequired: true,
          validate: [required, maxLength(40)]
        },
        {
          name: "template",
          label: "Template",
          classType: "primary",
          selectItems: this._templatesSelectData,
          placeholder: "Choose template",
          isRequired: true,
          validate: [required],
          elementType: "vue-select",
          extraClass: this.templateType === "letter" ? "letter" : "",
          extraClassRoot: "template-wrapper"
        },
        {
          name: "tags",
          label: "Tags",
          classType: "primary",
          multiple: true,
          elementType: "tags",
          selectItems: this.getTags,
          onAddHandler: this.handleAddTag,
          onRemoveHandler: tag => this.handleDeleteTag(tag),
          validate: [requiredTags],
          extraClass: "campaigns-create-tags",
          withDelete: true,
          maxLength: 255,
          onSearchAsyncHandler: input =>
            this.fetchFormTagsList({ search: input }),
          isLoading: this._isTagsLoading
        }
      ];
    },

    addressFields() {
      return [
        ...map(addressFormFields, item => {
          if (item.name === "country") {
            item.extraClassRoot = "campaigns-create";
          }

          item.layout = "full";
          item.classType = "primary";
          return item;
        })
      ];
    },

    useAccount() {
      return this.getValue("useAccount");
    },

    isLoading() {
      const { lettersTemplates, templates } = this.$store.state;
      return lettersTemplates.isLoading || templates.isLoading;
    },

    getTags() {
      return convertTagsToSelectListFormat(this.tagsFromStore("formTags"));
    },

    getTemplates() {
      return convertTemplatesToSelectFormat(this.getLiveTemplates);
    },

    isAddressValid() {
      return this.$store.getters["forms/isValid"](this.addressFormName);
    },

    addressFormValues() {
      return this.$store.getters["forms/getValuePairs"](this.addressFormName);
    },

    addressFormValid() {
      return this.$store.getters["forms/isValid"](this.addressFormName);
    },

    shouldUseAccount() {
      if (this.initialCampaignValues && this.initialCampaignValues.mainInfo) {
        return this.initialCampaignValues.mainInfo.useAccountAddress
          ? "yes"
          : "no";
      }
      return "yes";
    },

    ...mapGetters("account", ["getAccount"]),
    ...mapGetters("templates", {
      postcardsTemplatesList: "getLiveTemplates"
    }),
    ...mapGetters("lettersTemplates", {
      lettersTemplatesList: "templatesList"
    }),
    ...mapGetters("campaigns", ["getCampaign"]),
    ...mapGetters("tags", {
      tagsFromStore: "tags",
      isMainTagsLoading: "isLoading",
      isFormTagsLoading: "isFormTagsLoading"
    })
  },

  methods: {
    ...mapActions("contacts", [
      "fetchContact",
      "fetchTagsList",
      "deleteContact",
      "editContact"
    ]),

    ...mapActions("tags", ["fetchFormTagsList"])
  },

  created() {
    // Initialize form if it's not exists
    // to let auto-fill fields when clicked "Edit campaign"
    if (!this.getForm()) {
      this.registerForm({ formName: this.name, fields: this.fields });
      this.registerForm({
        formName: this.addressFormName,
        fields: this.addressFields
      });

      if (this.initialCampaignValues) {
        const { mainInfo, fromAddress } = this.initialCampaignValues;

        this.initializeForm({
          formName: this.name,
          fields: mainInfo
        });

        // address fields
        this.initializeForm({
          formName: this.addressFormName,
          fields: fromAddress
        });
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.campaign-address-fields {
  margin-top: 30px;
}

h1 {
  font-family: Lato-Regular, sans-serif;
  font-size: 22px;
  color: $primary-text-color;
  margin-bottom: 30px;
  text-align: left;
}

.generalInfo {
  display: grid;
  grid-template-rows: 95px 95px auto;
  grid-template-columns: 100%;

  & /deep/ .field-wrapper {
    margin-bottom: 0;
  }
}

.form-top-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.campaigns-create__address-wrapper {
  margin-bottom: 81px;

  h1 {
    margin-bottom: 0;
  }
}
</style>
